import React, { useState, useEffect, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Upload,
  Plus,
  Edit2,
  Trash2,
  Eye,
  FileIcon,
  X,
  Clock,
  Play,
} from "lucide-react";
import { useAuth } from "@/lib/auth-context";

interface Media {
  id: number;
  user_id: number;
  uuid: string;
  url: string;
  mime_type: string;
  file_size?: number;
  original_filename?: string;
  title?: string;
  description?: string;
  tags?: string[];
  r_user?: string;
  r_subreddit?: string;
  source?: string;
  people?: string[];
  time_watched: number;
  view_count: number;
  thumbnail_url?: string;
  thumbnail_generated: boolean;
  created_at: string;
  updated_at: string;
}

// Helper function to get display URL (thumbnail if available, otherwise original)
const getDisplayUrl = (media: Media): string => {
  if (media.thumbnail_url && media.thumbnail_generated) {
    return `/api/public/thumbnails/${media.uuid}`;
  }
  return `/api/public/media/${media.uuid}`;
};

// Helper function to get thumbnail URL
const getThumbnailUrl = (uuid: string): string => {
  return `/api/public/thumbnails/${uuid}`;
};

export function ManagePage() {
  const { user, token } = useAuth();
  const [media, setMedia] = useState<Media[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [editingMedia, setEditingMedia] = useState<Media | null>(null);

  // Pagination state
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const loadingRef = useRef<HTMLDivElement>(null);

  // Upload form state
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadTitle, setUploadTitle] = useState("");
  const [uploadDescription, setUploadDescription] = useState("");
  const [uploadTags, setUploadTags] = useState("");
  const [uploadRUser, setUploadRUser] = useState("");
  const [uploadRSubreddit, setUploadRSubreddit] = useState("");
  const [uploadSource, setUploadSource] = useState("");
  const [uploadPeople, setUploadPeople] = useState("");
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    fetchMedia();
  }, []);

  // Intersection Observer for infinite scrolling
  useEffect(() => {
    if (!loadingRef.current || !hasMore || isLoadingMore || loading) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          fetchMedia(page + 1, true);
        }
      },
      { threshold: 0.1 }
    );

    observer.observe(loadingRef.current);

    return () => {
      if (loadingRef.current) {
        observer.unobserve(loadingRef.current);
      }
    };
  }, [hasMore, isLoadingMore, loading, page]);

  const fetchMedia = async (pageNum: number = 1, append: boolean = false) => {
    try {
      if (pageNum === 1) {
        setLoading(true);
      } else {
        setIsLoadingMore(true);
      }

      const params = new URLSearchParams({
        page: pageNum.toString(),
        limit: "20",
        sortBy: "created_at",
        sortOrder: "desc",
      });

      const response = await fetch(`/api/media?${params}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error("Failed to fetch media");
      }

      const data = await response.json();

      if (append) {
        setMedia((prev) => [...prev, ...(data.media || [])]);
      } else {
        setMedia(data.media || []);
      }

      // Update pagination state
      setTotalCount(data.pagination?.total || 0);
      setHasMore(
        data.pagination && data.pagination.page < data.pagination.pages
      );
      setPage(pageNum);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unknown error");
    } finally {
      setLoading(false);
      setIsLoadingMore(false);
    }
  };

  const handleUpload = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!uploadFile) return;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", uploadFile);
      formData.append("title", uploadTitle || uploadFile.name);
      if (uploadDescription) formData.append("description", uploadDescription);
      if (uploadTags) formData.append("tags", uploadTags);
      if (uploadRUser) formData.append("rUser", uploadRUser);
      if (uploadRSubreddit) formData.append("rSubreddit", uploadRSubreddit);
      if (uploadSource) formData.append("source", uploadSource);
      if (uploadPeople) formData.append("people", uploadPeople);

      const response = await fetch("/api/media/upload", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Upload failed");
      }

      // Reset form
      setUploadFile(null);
      setUploadTitle("");
      setUploadDescription("");
      setUploadTags("");
      setUploadRUser("");
      setUploadRSubreddit("");
      setUploadSource("");
      setUploadPeople("");
      setShowUploadForm(false);

      // Reset pagination and refresh the media list
      setPage(1);
      setHasMore(true);
      fetchMedia(1, false); // Refresh from page 1
    } catch (err) {
      setError(err instanceof Error ? err.message : "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const handleUpdate = async (media: Media) => {
    try {
      const response = await fetch(`/api/media/${media.id}`, {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          title: media.title,
          description: media.description,
          tags: media.tags,
          r_user: media.r_user,
          r_subreddit: media.r_subreddit,
          source: media.source,
          people: media.people,
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to update media");
      }

      setEditingMedia(null);
      // Reset pagination and refresh the media list
      setPage(1);
      setHasMore(true);
      fetchMedia(1, false);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Update failed");
    }
  };

  const handleDelete = async (mediaId: number) => {
    if (!confirm("Are you sure you want to delete this media?")) return;

    try {
      const response = await fetch(`/api/media/${mediaId}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error("Failed to delete media");
      }

      // Reset pagination and refresh the media list
      setPage(1);
      setHasMore(true);
      fetchMedia(1, false);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Delete failed");
    }
  };

  const getMediaUrl = (uuid: string) => {
    // Use public UUID-based endpoint (no auth required)
    return `/api/public/media/${uuid}`;
  };

  const getThumbnailUrl = (uuid: string) => {
    // Use public thumbnail endpoint (no auth required)
    return `/api/public/thumbnails/${uuid}`;
  };

  const formatFileSize = (bytes: number) => {
    const sizes = ["Bytes", "KB", "MB", "GB"];
    if (bytes === 0) return "0 Bytes";
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round((bytes / Math.pow(1024, i)) * 100) / 100 + " " + sizes[i];
  };

  const formatTimeWatched = (seconds: number) => {
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    if (minutes < 60) return `${minutes}m ${remainingSeconds}s`;
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return `${hours}h ${remainingMinutes}m`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading media...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold">Manage Media</h1>
        <Button
          onClick={() => setShowUploadForm(!showUploadForm)}
          className="flex items-center gap-2"
        >
          {showUploadForm ? (
            <X className="h-4 w-4" />
          ) : (
            <Plus className="h-4 w-4" />
          )}
          {showUploadForm ? "Cancel" : "Upload Media"}
        </Button>
      </div>

      {error && (
        <Card className="border-destructive">
          <CardContent className="pt-6">
            <p className="text-destructive">{error}</p>
            <Button onClick={() => setError(null)} className="mt-2">
              Dismiss
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Upload Form */}
      {showUploadForm && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Upload className="h-5 w-5" />
              Upload New Media
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleUpload} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="file">File</Label>
                <Input
                  id="file"
                  type="file"
                  accept="image/*,video/*"
                  onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="title">Title</Label>
                <Input
                  id="title"
                  value={uploadTitle}
                  onChange={(e) => setUploadTitle(e.target.value)}
                  placeholder="Enter title (optional)"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Input
                  id="description"
                  value={uploadDescription}
                  onChange={(e) => setUploadDescription(e.target.value)}
                  placeholder="Enter description (optional)"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="tags">Tags</Label>
                  <Input
                    id="tags"
                    value={uploadTags}
                    onChange={(e) => setUploadTags(e.target.value)}
                    placeholder="tag1, tag2, tag3"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="people">People</Label>
                  <Input
                    id="people"
                    value={uploadPeople}
                    onChange={(e) => setUploadPeople(e.target.value)}
                    placeholder="person1, person2"
                  />
                </div>
              </div>

              {/* Source information fields */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="rUser">Reddit User</Label>
                  <Input
                    id="rUser"
                    value={uploadRUser}
                    onChange={(e) => setUploadRUser(e.target.value)}
                    placeholder="u/username"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="rSubreddit">Subreddit</Label>
                  <Input
                    id="rSubreddit"
                    value={uploadRSubreddit}
                    onChange={(e) => setUploadRSubreddit(e.target.value)}
                    placeholder="r/subreddit"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="source">Other Source</Label>
                  <Input
                    id="source"
                    value={uploadSource}
                    onChange={(e) => setUploadSource(e.target.value)}
                    placeholder="Source URL or description"
                  />
                </div>
              </div>

              <Button
                type="submit"
                disabled={!uploadFile || uploading}
                className="w-full"
              >
                {uploading ? "Uploading..." : "Upload Media"}
              </Button>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Count of media */}
      <div className="text-sm text-muted-foreground">
        {totalCount > 0 ? (
          <>
            Showing {media.length} of {totalCount} media files
            {hasMore && (
              <span className="ml-2 text-blue-600">
                (scroll down to load more)
              </span>
            )}
          </>
        ) : (
          `${media.length} media files uploaded`
        )}
      </div>

      {/* Media List */}
      <div className="space-y-4">
        {media.length === 0 ? (
          <Card>
            <CardContent className="pt-6 text-center">
              <p className="text-muted-foreground mb-4">
                No media uploaded yet
              </p>
              <Button onClick={() => setShowUploadForm(true)}>
                Upload your first media file
              </Button>
            </CardContent>
          </Card>
        ) : (
          media.map((item) => (
            <Card key={item.id}>
              <CardContent className="p-6">
                <div className="flex flex-col md:flex-row gap-4">
                  {/* Media Preview */}
                  <div className="w-full md:w-32 h-32 bg-muted rounded overflow-hidden flex-shrink-0">
                    {item.mime_type.startsWith("image/") ? (
                      <img
                        src={
                          item.thumbnail_generated
                            ? getThumbnailUrl(item.uuid)
                            : getMediaUrl(item.uuid)
                        }
                        alt={item.title || item.original_filename || "Media"}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          // Fallback to original media if thumbnail fails to load
                          const target = e.target as HTMLImageElement;
                          if (target.src !== getMediaUrl(item.uuid)) {
                            target.src = getMediaUrl(item.uuid);
                          }
                        }}
                      />
                    ) : item.mime_type.startsWith("video/") ? (
                      <div className="relative w-full h-full">
                        <img
                          src={
                            item.thumbnail_generated
                              ? getThumbnailUrl(item.uuid)
                              : getMediaUrl(item.uuid)
                          }
                          alt={
                            item.title ||
                            item.original_filename ||
                            "Video thumbnail"
                          }
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            // Fallback to original media if thumbnail fails to load
                            const target = e.target as HTMLImageElement;
                            if (target.src !== getMediaUrl(item.uuid)) {
                              target.src = getMediaUrl(item.uuid);
                            }
                          }}
                        />
                        <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                          <div className="bg-black/50 rounded-full p-2">
                            <Play className="h-6 w-6 text-white" />
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <FileIcon className="h-8 w-8 text-muted-foreground" />
                      </div>
                    )}
                  </div>

                  {/* Media Info */}
                  <div className="flex-1 space-y-2">
                    {editingMedia?.id === item.id ? (
                      // Edit form
                      <div className="space-y-3">
                        <Input
                          value={editingMedia.title}
                          onChange={(e) =>
                            setEditingMedia({
                              ...editingMedia,
                              title: e.target.value,
                            })
                          }
                          placeholder="Title"
                        />
                        <Input
                          value={editingMedia.description || ""}
                          onChange={(e) =>
                            setEditingMedia({
                              ...editingMedia,
                              description: e.target.value,
                            })
                          }
                          placeholder="Description"
                        />
                        <Input
                          value={editingMedia.tags?.join(", ") || ""}
                          onChange={(e) =>
                            setEditingMedia({
                              ...editingMedia,
                              tags: e.target.value
                                .split(",")
                                .map((t) => t.trim())
                                .filter(Boolean),
                            })
                          }
                          placeholder="Tags (comma-separated)"
                        />
                        <Input
                          value={editingMedia.people?.join(", ") || ""}
                          onChange={(e) =>
                            setEditingMedia({
                              ...editingMedia,
                              people: e.target.value
                                .split(",")
                                .map((p) => p.trim())
                                .filter(Boolean),
                            })
                          }
                          placeholder="People (comma-separated)"
                        />
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                          <Input
                            value={editingMedia.r_user || ""}
                            onChange={(e) =>
                              setEditingMedia({
                                ...editingMedia,
                                r_user: e.target.value,
                              })
                            }
                            placeholder="Reddit User (u/username)"
                          />
                          <Input
                            value={editingMedia.r_subreddit || ""}
                            onChange={(e) =>
                              setEditingMedia({
                                ...editingMedia,
                                r_subreddit: e.target.value,
                              })
                            }
                            placeholder="Subreddit (r/name)"
                          />
                          <Input
                            value={editingMedia.source || ""}
                            onChange={(e) =>
                              setEditingMedia({
                                ...editingMedia,
                                source: e.target.value,
                              })
                            }
                            placeholder="Other Source"
                          />
                        </div>
                        <div className="flex gap-2">
                          <Button
                            onClick={() => handleUpdate(editingMedia)}
                            size="sm"
                          >
                            Save
                          </Button>
                          <Button
                            onClick={() => setEditingMedia(null)}
                            variant="outline"
                            size="sm"
                          >
                            Cancel
                          </Button>
                        </div>
                      </div>
                    ) : (
                      // Display mode
                      <>
                        <h3 className="text-lg font-semibold">{item.title}</h3>
                        {item.description && (
                          <p className="text-muted-foreground">
                            {item.description}
                          </p>
                        )}
                        <div className="flex flex-wrap gap-2 text-sm">
                          <span className="text-muted-foreground">
                            {formatFileSize(item.file_size)}
                          </span>
                          <span className="text-muted-foreground">•</span>
                          <span className="text-muted-foreground">
                            {item.mime_type}
                          </span>
                          <span className="text-muted-foreground">•</span>
                          <span className="flex items-center gap-1">
                            <Eye className="h-3 w-3" />
                            {item.view_count} views
                          </span>
                          {item.time_watched > 0 && (
                            <>
                              <span className="text-muted-foreground">•</span>
                              <span className="flex items-center gap-1">
                                <Clock className="h-3 w-3" />
                                {formatTimeWatched(item.time_watched)}
                              </span>
                            </>
                          )}
                        </div>
                        {item.tags && item.tags.length > 0 && (
                          <div className="flex flex-wrap gap-1">
                            {item.tags.map((tag) => (
                              <span
                                key={tag}
                                className="px-2 py-1 bg-secondary text-secondary-foreground text-xs rounded"
                              >
                                {tag}
                              </span>
                            ))}
                          </div>
                        )}

                        {/* Source links */}
                        {(item.r_user || item.r_subreddit || item.source) && (
                          <div className="flex flex-wrap gap-1 text-xs">
                            {item.r_user && (
                              <a
                                href={`https://reddit.com/user/${item.r_user}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-500 hover:text-blue-600 underline"
                              >
                                u/{item.r_user}
                              </a>
                            )}
                            {item.r_subreddit && (
                              <a
                                href={`https://reddit.com/r/${item.r_subreddit}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-500 hover:text-blue-600 underline"
                              >
                                r/{item.r_subreddit}
                              </a>
                            )}
                            {!item.r_user &&
                              !item.r_subreddit &&
                              item.source && (
                                <a
                                  href={item.source}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-blue-500 hover:text-blue-600 underline truncate"
                                  title={item.source}
                                >
                                  Source
                                </a>
                              )}
                          </div>
                        )}
                        <div className="flex gap-2 pt-2">
                          <Button
                            onClick={() => setEditingMedia(item)}
                            variant="outline"
                            size="sm"
                          >
                            <Edit2 className="h-4 w-4 mr-2" />
                            Edit
                          </Button>
                          <Button
                            onClick={() => handleDelete(item.id)}
                            variant="destructive"
                            size="sm"
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Delete
                          </Button>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}

        {/* Loading indicator for infinite scroll */}
        {hasMore && (
          <div
            ref={loadingRef}
            className="flex justify-center items-center py-8"
          >
            {isLoadingMore ? (
              <div className="flex items-center space-x-2 text-muted-foreground">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
                <span>Loading more media...</span>
              </div>
            ) : (
              <div className="text-muted-foreground text-sm">
                Scroll down to load more
              </div>
            )}
          </div>
        )}

        {/* End of results indicator */}
        {!hasMore && media.length > 0 && (
          <div className="text-center py-8 text-muted-foreground text-sm">
            You've reached the end of your media collection
          </div>
        )}
      </div>
    </div>
  );
}
